// Full-screen preview for a downloaded file: image / video / audio / PDF /
// text render inline; everything else offers Download + native Share.
import { useEffect, useState } from 'react';
import { formatSize } from '../format';
import { canNativeShareFile, shareFileNative } from '../nativeShare';

export interface PreviewState {
  file: File;
  url: string;
  mime: string;
  streaming?: boolean;
  totalSize?: number;
}

export interface PreviewModalProps {
  preview: PreviewState;
  onClose: () => void;
}

export function saveBlob(name: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

function extOf(name: string): string {
  const i = name.lastIndexOf('.');
  return i >= 0 ? name.slice(i + 1).toLowerCase() : '';
}

function previewKind(file: File, mime: string): 'image' | 'video' | 'audio' | 'pdf' | 'text' | 'other' {
  const base = mime.split(';')[0].split('/')[0];
  if (base === 'image' || base === 'video' || base === 'audio') return base;
  if (mime === 'application/pdf') return 'pdf';
  if (base === 'text' || mime === 'application/json') return 'text';
  switch (extOf(file.name)) {
    case 'png':
    case 'jpg':
    case 'jpeg':
    case 'gif':
    case 'webp':
    case 'bmp':
    case 'svg':
    case 'avif':
      return 'image';
    case 'mp4':
    case 'm4v':
    case 'webm':
    case 'mov':
    case 'ogv':
      return 'video';
    case 'mp3':
    case 'm4a':
    case 'aac':
    case 'wav':
    case 'ogg':
    case 'oga':
    case 'flac':
      return 'audio';
    case 'pdf':
      return 'pdf';
    case 'txt':
    case 'log':
    case 'md':
    case 'csv':
    case 'json':
    case 'yml':
    case 'yaml':
    case 'ini':
    case 'cfg':
    case 'htm':
    case 'html':
    case 'xml':
      return 'text';
    default:
      return 'other';
  }
}

function isMobilePreview(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
}

function openPdfExternally(url: string, file: File): void {
  const opened = window.open(url, '_blank', 'noopener,noreferrer');
  if (!opened) {
    saveBlob(file.name, file);
  }
}

function PdfPreview({ url, name, file }: { url: string; name: string; file: File }): JSX.Element {
  const mobile = isMobilePreview();
  return (
    <div className={`pdf-view ${mobile ? 'pdf-view-mobile' : ''}`}>
      {!mobile && (
        <object data={url} type="application/pdf" className="pdf-object" aria-label={name}>
          <embed src={url} type="application/pdf" className="pdf-embed" />
        </object>
      )}
      {mobile && (
        <div className="pdf-mobile-panel">
          <p className="hint">Mobile browsers often block embedded PDFs. Open the file in the system viewer.</p>
          <button type="button" className="btn primary pdf-open-btn" onClick={() => openPdfExternally(url, file)}>
            Open PDF
          </button>
          <object data={url} type="application/pdf" className="pdf-object pdf-object-fallback">
            <embed src={url} type="application/pdf" className="pdf-embed" />
          </object>
        </div>
      )}
    </div>
  );
}

export function PreviewModal({ preview, onClose }: PreviewModalProps): JSX.Element {
  const { file, url, mime, streaming, totalSize } = preview;
  const [text, setText] = useState<string | null>(null);
  const kind = previewKind(file, mime);

  useEffect(() => {
    if (!streaming && (mime.startsWith('text/') || mime === 'application/json')) {
      let alive = true;
      file
        .text()
        .then((t) => {
          if (alive) setText(t.slice(0, 200_000));
        })
        .catch(() => undefined);
      return () => {
        alive = false;
      };
    }
    return undefined;
  }, [file, mime, streaming]);

  const share = () => {
    void shareFileNative(file).catch(() => undefined);
  };

  const sizeLabel = streaming && totalSize != null ? formatSize(totalSize) : formatSize(file.size);

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" aria-label={file.name}>
      <div className={`modal ${kind === 'pdf' ? 'modal-pdf' : ''}`}>
        <div className="modal-head">
          <span className="modal-title">{file.name}</span>
          <span className="modal-sub">
            {sizeLabel} · {mime}
            {streaming ? ' · streaming' : ''}
          </span>
        </div>
        <div className="modal-body">
          {kind === 'image' && <img src={url} alt={file.name} />}
          {kind === 'video' && <video src={url} controls playsInline autoPlay />}
          {kind === 'audio' && <audio src={url} controls autoPlay />}
          {kind === 'pdf' && !streaming && <PdfPreview url={url} name={file.name} file={file} />}
          {kind === 'pdf' && streaming && (
            <div className="preview-none">
              <p>PDF preview is available after the download finishes.</p>
            </div>
          )}
          {kind === 'text' && <pre>{text ?? 'Loading…'}</pre>}
          {kind === 'other' && (
            <div className="preview-none">
              <p>No inline preview for {mime}.</p>
              <p className="hint">Use Download or Share below.</p>
            </div>
          )}
        </div>
        <div className="modal-actions">
          <button type="button" className="btn ghost" onClick={onClose}>
            Close
          </button>
          {kind === 'pdf' && !streaming && (
            <button type="button" className="btn" onClick={() => openPdfExternally(url, file)}>
              Open in browser
            </button>
          )}
          {!streaming && canNativeShareFile(file) && (
            <button type="button" className="btn" onClick={share}>
              Share…
            </button>
          )}
          {!streaming && (
            <button type="button" className="btn primary" onClick={() => saveBlob(file.name, file)}>
              Download
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
